import { Component, OnInit } from '@angular/core';
import { fromEvent as observableFromEvent, Subscription } from 'rxjs';
import { filter, mergeMap } from 'rxjs/operators';

import { Algorithm } from '../../../../../../models/algorithm';
import { Company } from '../../../../../../models/company';
import { Operation } from '../../../../../../models/operation';
import { PeriodOperation } from '../../../../../../models/period-operation';

import { AlertService } from '../../../../../../services/alert.service';
import { SharedDataService } from '../../../../../../services/shared-data.service';
import { BorrowerService } from '../../../../../../services/borrower.service';
import { AlgorithmsService } from '../../../../../../services/algorithms.service';
import { DataFrameService } from '../../../../../../services/dataframes.service';
import { DataViewService } from '../../../../../../services/data-view.service';

import { MONTHS_SHORT } from '../../../../../../utils/constants';
import { AutoUnsubscribe } from '../../../../../../decorators/auto-unsubscribe';
import { isInRange, unsubArr } from '../../../../../../utils/functions';
import { DataViewRuleType } from '../../../../../../utils/enums';
import { CommonFunctions } from '../../../../../../utils/common-functions';

interface Periods {
  head: Array<any>,
  data: Array<any>
}

@Component({
    selector: 'app-compliance-covenant-table',
    templateUrl: './covenant-table.component.html',
    styleUrls: ['./covenant-table.component.scss'],
    standalone: false
})
@AutoUnsubscribe('subsArr$')
export class ComplianceCovenantTableComponent implements OnInit {
  format: number;
  subsArr$: Subscription[] = [];
  previousCell: any;
  currentReqValueHtmlDiv: HTMLElement;
  company: Company;
  algorithm: Algorithm;
  operations: Operation[] = [];
  months = MONTHS_SHORT;
  data: Periods = {head: [], data: []};
  cv_data_view: any;
  dfg: any;
  loading = true;
  loadingSave = false;
  keyboardSubs: Subscription[] = [];

  constructor(private _companyService: BorrowerService,
              private _dataViewService: DataViewService,
              private _dataFrameService: DataFrameService,
              private _algorithmService: AlgorithmsService,
              private _sharedService: SharedDataService,
              private _alertService: AlertService,
  ) {

  }

  ngOnInit() {
    this.subsArr$.push(this._sharedService.company$.pipe(
      filter(company => company !== null && (this.company ? company.id !== this.company.id : true)),
      mergeMap((company: Company) => {
        this.company = company;
        return this._dataViewService.getDataView(this.company.id, 'CALCULATION', false, true);
      }),
      mergeMap((data: any) => {
        this.cv_data_view = data;
        return this._dataFrameService.getDataFrameGenerator('CALCULATION', false, this.company.id)
      }),
      ).subscribe(data => {

        this.dfg = data;
        this.operations = this.dfg.formulas || [];
        this.data = this._prepareData(this.cv_data_view);
        this.loading = false;
      }),
    )
    ;
    this._subscribeForFormat();
  }


  /**
   * handler to open popup to company-form required values
   */
  editRequiredValue(selectedCell: HTMLElement, periodOperation: PeriodOperation, operation: Operation, headDate: string) {
    if (this.previousCell) {
      this._restoreEditedCell(this.previousCell, true);
    }
    unsubArr(this.keyboardSubs);
    this.previousCell = selectedCell;
    this.currentReqValueHtmlDiv = <HTMLElement>selectedCell.children[0];
    this.currentReqValueHtmlDiv.style.display = 'none';
    const cellInput = CommonFunctions.createInputElement();
    selectedCell.classList.add('selected-cell');
    selectedCell.appendChild(cellInput);
    cellInput.value = periodOperation.periodOperationId === null || periodOperation.isInherited || !periodOperation.requiredValue
      ? '' : periodOperation.requiredValue.toString();
    cellInput.focus();
    const saveOrRestoreReqValueFunction = () => {
      const isValidRequiredValue = this._validateRequiredValue(cellInput.value);
      if (isValidRequiredValue) {
        if (parseFloat(cellInput.value) === periodOperation.requiredValue ||
          (!cellInput.value && periodOperation.requiredValue === null)
        ) {
          this._restoreEditedCell(this.previousCell, true);
        } else {
          this._saveReqValue(operation, headDate, cellInput.value, periodOperation);
        }
      }
      unsubArr(this.keyboardSubs);
    };
    this._handleKeyboardEvents(cellInput, saveOrRestoreReqValueFunction);
    this._listenForClicksToRestoreCell(saveOrRestoreReqValueFunction);
  }

  scrollTable() {
    let body, header, sidebar;
    body = document.getElementById('body-table');
    sidebar = document.getElementById('sidebar-table');
    header = document.getElementById('header-table');
    sidebar.setAttribute('style', 'margin-top:' + (-body.scrollTop) + 'px;');
    header.setAttribute('style', 'margin-left:' + (-body.scrollLeft) + 'px;');
  }

  translatePeriodType(reportingPeriod: string) {
    return reportingPeriod.split('.')[1];
  }

  /**
   * handler to remove styles from selected cell
   * @param cell
   * @param restoreDefaultText
   */
  private _restoreEditedCell(cell: HTMLElement, restoreDefaultText: boolean) {
    cell.classList.remove('selected-cell');
    const cellInput = cell.getElementsByClassName('cell-input')[0];
    if (cellInput) {
      cellInput.parentElement.removeChild(cellInput);
    }
    if (this.currentReqValueHtmlDiv) {
      this.currentReqValueHtmlDiv.style.display = 'inline';
    }
  }

  /**
   * main place to handle keyboard presses and react accordingly
   * @param {HTMLInputElement} cellInput
   * @param {() => void} saveOrRestoreReqValueFunction
   * @private
   */
  private _handleKeyboardEvents(cellInput: HTMLInputElement, saveOrRestoreReqValueFunction: () => void) {
    this.keyboardSubs.push(observableFromEvent(cellInput, 'keydown')
      .subscribe((ev: any) => {
        /* allow only numbers and Period, (don't block functional keys as well) */
        if (!(isInRange(ev.keyCode, 8, 64) || isInRange(ev.keyCode, 96, 105)
          || ev.keyCode === 190 || ev.keyCode === 189 || ev.keyCode === 109 || ev.keyCode === 110) || ev.shiftKey) {
          ev.preventDefault();
        }
        if (ev.keyCode === 13) {
          saveOrRestoreReqValueFunction();
          unsubArr(this.keyboardSubs);
        }
      }));
  }

  /**
   * handler to validate and check new Required values
   * @param {Operation} operation
   * @param headDate
   * @param {string} reqValueText
   * @param {PeriodOperation} periodOperation
   */
  private _saveReqValue(operation: Operation, headDate: any, reqValueText: string, periodOperation: PeriodOperation) {
    const newReqValue = reqValueText.trim().length ? parseFloat(reqValueText.trim()) : '';
    const dataViewId = this.cv_data_view.id;
    const ruleEquation = `=${newReqValue}`;
    this.subsArr$.push(
      this._dataViewService.updateDataViewRule(dataViewId, DataViewRuleType.COVENANT, periodOperation.reportingPeriod, periodOperation.lineItemId, ruleEquation)
        .pipe(
          mergeMap(() => {
            this.loadingSave = true;
            return this._dataViewService.getDataView(this.company.id, 'CALCULATION', false, true)
          }),
        )
        .subscribe(data => {
          this.cv_data_view = data;
          this.data = this._prepareData(this.cv_data_view);
          this.loadingSave = false;
        }),
    );
  }

  /**
   * handler to prepare data
   * @param data
   * @returns {any}
   * @private
   */
  private _prepareData(data): Periods {
    const prepared = Object();
    const head = [];
    const body = [];

    this.cv_data_view.frames.forEach((df) => {
      head.push({
        statementDate: df.statementDate,
        reportingPeriod: df.reportingPeriod
       });
    });

    const rules = this.cv_data_view.rules;
    const lastRequiredValue = {};

    this.dfg.formulas.forEach((formula: any) => {
      const title = formula.lineItem.name;

      const lineItemRules = rules[formula.lineItem.id.toString()];

      const requirements = [];
      head.forEach((header) => {
          if (!lineItemRules || !lineItemRules.periods[header.reportingPeriod] || !lineItemRules.periods[header.reportingPeriod].covenantEquation) {
            // we dont have a set compliance required value
            let isInherited = false;
            const requiredValue = lastRequiredValue[formula.lineItem.id];
            if (!!requiredValue) {
              isInherited = true;
            }
            requirements.push({
              lineItemId: formula.lineItem.id,
              uniqueKey: formula.lineItem.uniqueKey,
              requiredValue,
              isInherited,
              reportingPeriod: header.reportingPeriod
            });
          } else {
            const periodRules = lineItemRules.periods[header.reportingPeriod];
            const requiredValue =  !periodRules.covenantEquation ? null : parseFloat(periodRules.covenantEquation.substr(1));
            requirements.push({
              lineItemId: formula.lineItem.id,
              uniqueKey: formula.lineItem.uniqueKey,
              requiredValue: requiredValue,
              isInherited: false,
              reportingPeriod: header.reportingPeriod
            });
            if (requiredValue != null) {
               lastRequiredValue[formula.lineItem.id] = requiredValue;
            }
          }
      });

      body.push({
        title,
        requirements
      });
    });

    prepared.head = head;
    prepared.data = body;
    return prepared;
  }

  /**
   * helper to subscribe for format that is chosen
   * for proper saving of required value
   */
  private _subscribeForFormat() {
    this.subsArr$.push(this._sharedService.currencyDigitsToTrim$.subscribe(v => {
      this.format = v;
    }))
  }

  /**
   * helper to listen for mouseUp events and restore previously selected cell
   * to the way unselected cell looks
   */
  private _listenForClicksToRestoreCell(saveOrRestoreReqValueFunction: () => void) {
    this.keyboardSubs.push(observableFromEvent(document, 'mouseup').pipe(
      filter(() => this.previousCell))
      .subscribe(() => {
        unsubArr(this.keyboardSubs);
        saveOrRestoreReqValueFunction();
      }),
    );
  }

  /**
   * helper to check if value entered by user is valid for requiredValue field
   * @param reqValue
   * @returns {boolean}
   */
  private _validateRequiredValue(reqValue: string): boolean {
    // special case for an empty string, allowing to return true to be able to restore value to null in database
    if (!reqValue.length) {
      return true;
    }
    const isNotValid = isNaN(Number(reqValue));
    const parsedToIntReqValue = parseFloat(reqValue);
    if (isNotValid) {
      this._alertService.warning('Cell can contain 0-9 and period only');
    }
    if (parsedToIntReqValue < -100000000 || parsedToIntReqValue >= 100000000) {
      this._alertService.warning('Required value should be an integer more than -100 000 000 and less than 100 000 000');
    }
    return (!isNotValid && parsedToIntReqValue > -100000000 && parsedToIntReqValue <= 100000000);
  }
}
